import { Spinner } from 'react-bootstrap';
import nodePath from 'path';
import { css, styled, useAsync } from '@topwrite/common';
import Tooltip from '../../components/tooltip';
import { BsTrash3 } from 'react-icons/bs';

interface Props {
    className?: string;
    name: string;
    loading?: boolean;
    error?: string;
    onRemove?: () => void;
}

const Icon = function({ ext }: { ext: string }) {

    const { result } = useAsync(async (ext: string) => {
        const img = await import(`../../images/file/${ext}.svg`);
        return img.default;
    }, [ext]);

    if (!result) {
        return null;
    }

    return <img width={16} height={16} src={result} />;
};

export default function FileItem({ className, name, loading, error, onRemove }: Props) {
    const ext = nodePath.extname(name).substring(1);
    const item = <Container className={className} $error={!!error}>
        <FileIcon>
            <Icon ext={ext} />
            {loading && <Spinner variant={'primary'} />}
        </FileIcon>
        <FileDesc>{name}</FileDesc>
        {onRemove && <RemoveIcon onClick={(e) => {
            e.preventDefault();
            onRemove();
        }}><BsTrash3 /></RemoveIcon>}
    </Container>;

    if (error) {
        return <Tooltip placement='top' tooltip={error}>
            {item}
        </Tooltip>;
    }

    return item;
}

const RemoveIcon = styled.div`
    width: 1rem;
    height: 1rem;
    align-items: center;
    justify-content: center;
    border-radius: .25rem;
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--bs-secondary);
    display: flex;

    &:hover {
        color: var(--bs-danger);
        background-color: var(--bs-danger-bg-subtle);
    }
`;

const FileDesc = styled.div`
    flex: 1;
    overflow: hidden;
    font-size: 12px;
    color: var(--bs-secondary);
    line-height: 20px;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-bottom: 0;
`;

const FileIcon = styled.div`
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bs-gray-200);
    border-radius: 3px;

    .spinner-border {
        position: absolute;
    }

`;

const Container = styled.div<{ $error: boolean }>`
    display: flex;
    gap: .5rem;
    padding: .5rem;
    border: var(--bs-border-width) var(--bs-border-style) var(--bs-border-color);
    border-radius: var(--bs-border-radius-lg);
    align-items: center;
    background: #FFF;

    &:hover {
        border-color: var(--bs-primary);
    }

    ${props => props.$error && css`
        border-color: var(--bs-danger) !important;
    `}
`;
